import { Component, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';
import { IAreaGroup, ISelectOption } from 'app/models/filter-form.models';
import { IAutocompleteOption } from 'app/models/filter.models';
import { AuthService } from 'app/services/auth.service';
import { DepartmentService } from 'app/services/department.service';
import { FilterService } from 'app/services/filter.service';
import { makeControlDirty, mapValuesToOptions } from 'app/utils/form.utils';
import { FilterItemComponent } from '../filter-item/filter-item.component';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { MultiSelectWithSearchComponent } from '../../form/multiselect-with-search/multiselect-with-search.component';

@Component({
  selector: 'app-areas-tab',
  templateUrl: './areas-tab.component.html',
  styleUrls: ['./areas-tab.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [
    FormsModule,
    FilterItemComponent,
    MatFormField,
    MatSelect,
    NgFor,
    NgIf,
    MatOption,
    MultiSelectWithSearchComponent,
  ],
})
export class AreasTabComponent implements OnInit {
  @ViewChild('area') ngModelGroup!: NgModelGroup;

  private departmentName?: string;

  areaGroup: IAreaGroup = {
    zipCodes: undefined,
    cities: undefined,
    departmentId: undefined,
  };

  municipality?: string;
  councilDistrict?: string;
  substation?: string;
  sapdDistrict?: string;

  cityOptions: IAutocompleteOption[] = [];
  zipCodeOptions: IAutocompleteOption[] = [];
  departmentOptions: ISelectOption[] = [];

  constructor(
    private filterService: FilterService,
    private departmentService: DepartmentService,
    private authService: AuthService,
  ) {}

  isLoading = false;
  isMunicipalityLoading = true;

  get zipCodesValue(): string {
    return this.areaGroup.zipCodes?.map((x) => x.value).join(', ') ?? '';
  }

  get citiesValue(): string | undefined {
    return (
      this.areaGroup.cities
        ?.filter((x) => x && x.value)
        .map((x) => x.value)
        .join(', ') ?? ''
    );
  }

  get departmentValue(): string | undefined {
    const name = this.departmentOptions.find(({ value }) => value === this.areaGroup.departmentId)?.viewValue;
    return this.isAdmin ? name : undefined;
  }

  get isAdmin() {
    return this.authService.isAdmin;
  }

  ngOnInit(): void {
    const filters = this.filterService.appliedFilters;
    const departmentId = this.isAdmin ? filters.departmentId : undefined;

    this.filterService.getAreas(departmentId).subscribe((filters) => {
      this.cityOptions = mapValuesToOptions(filters.cities);
      this.zipCodeOptions = mapValuesToOptions(filters.zipCodes);
    });
    if (this.isMunicipalityLoading) {
      this.departmentOptionsValue();
    }

    if (this.isAdmin) {
      this.areaGroup.departmentId = filters.departmentId;
    }

    if (!filters.locationFilter) return;

    this.areaGroup.cities = mapValuesToOptions(filters.locationFilter.cities);
    this.areaGroup.zipCodes = mapValuesToOptions(filters.locationFilter.zipCodes);
  }

  /** Saved the code in case if Municipality filter is needed again. */
  departmentOptionsValue() {
    if (this.isAdmin) {
      this.departmentService.getDepartments().subscribe((departments) => {
        this.departmentOptions = departments.map(({ id, name }) => ({
          value: id,
          viewValue: name,
        }));
      });
    } else {
      this.authService.getDepartment().subscribe(({ id, name }) => {
        this.departmentOptions = [
          {
            value: id,
            viewValue: name,
          },
        ];
      });
    }
  }

  onDepartmentChange(id: string) {
    if (!this.isAdmin) return;

    this.isLoading = true;
    this.areaGroup.departmentId = id;

    this.filterService.getAreas(id).subscribe((filters) => {
      this.cityOptions = mapValuesToOptions(filters.cities);
      this.zipCodeOptions = mapValuesToOptions(filters.zipCodes);
      this.areaGroup.cities = undefined;
      this.areaGroup.zipCodes = undefined;
      this.isLoading = false;
    });
  }

  clearFilter(filter: string) {
    return () => {
      makeControlDirty(this.ngModelGroup.control);

      switch (filter) {
        case 'departmentId':
          this.areaGroup.departmentId = undefined;
          break;
        case 'cities':
          this.areaGroup.cities = undefined;
          break;
        case 'zipCodes':
          this.areaGroup.zipCodes = undefined;
          break;
      }
    };
  }
}
